import {ssrSafeWindow} from '@github-ui/ssr-utils'

import type {GraphQLDataResponse} from './graphql-data-response'

/** the payload shape `handleRepositoryDataError` expects, where `T` is the route's `data`. */
export type RepositoryDataResponse<T> = GraphQLDataResponse<T>

/** Reloads the page with a loop-safe `reload=1` param so the server can re-render. */
export function reloadPage() {
  const reloadParameter = ['reload', '1'] as const
  const url = new URL(window.location.href, window.location.origin)
  if (url.searchParams.has(...reloadParameter)) {
    return
  }
  url.searchParams.set(...reloadParameter)
  ssrSafeWindow?.location.assign(url)
}

/**
 * DataRouter counterpart to `@github-ui/relay-error-helpers`, shared across repository-scoped routes.
 *
 * Only SAML authorization errors (SAML and the legacy FORBIDDEN type) are handled: they're the
 * repository errors returned at HTTP 200.
 * NOT_FOUND/AUTHENTICATION 404 upstream via `AbstractRepositoryController`, so `queryFnFetch` throws
 * to the route error boundary before this runs.
 *
 * @returns `data` on success, or `undefined` after handling SAML/FORBIDDEN; throws otherwise.
 *   Pass `shouldReloadOnError: false` to skip reloading in embedded contexts.
 */
export function handleRepositoryDataError<T extends {repository?: unknown}>(
  response: RepositoryDataResponse<T> | null | undefined,
  options?: {shouldReloadOnError?: boolean},
): T | undefined {
  const {shouldReloadOnError = true} = options || {}
  const data = response?.data
  if (data?.repository) {
    return data
  }

  const errors = response?.errors
  const hasSAMLError = errors?.some(
    e => e && typeof e === 'object' && 'type' in e && (e.type === 'SAML' || e.type === 'FORBIDDEN'),
  )
  if (hasSAMLError) {
    if (shouldReloadOnError) {
      reloadPage()
    }
    return undefined
  }

  throw new Error(`Repository data error: ${JSON.stringify(errors)}`)
}
